export const BANNER_SIZES = ['small', 'medium', 'large'];
export const DEFAULT_BANNER_SIZE = 'small';
export const BANNER_POSITIONS = ['left', 'center', 'right'];
export const DEFAULT_BANNER_POSITION = 'left';
export const DEFAULT_CTA_OPACITY = 30;

const HEX_REGEX = /^([0-9a-f]{3}|[0-9a-f]{6})$/i;
const HEX_CHARS_REGEX = /^[0-9a-f]+$/i;
// rgb()/hsl() in either comma or space syntax. A trailing "/ alpha" is dropped
// so the CTA opacity always wins over whatever alpha the color carried.
const FUNCTIONAL_COLOR_REGEX = /^(rgb|hsl)a?\(\s*([^/)]+?)\s*(?:\/[^)]*)?\)$/i;

const CTA_TYPES = {
  external: 'external',
  'external-link': 'external',
  internal: 'internal',
  'internal-link': 'internal',
  jump: 'jump',
  'jump-link': 'jump',
};

export function getSize(size) {
  const normalized = String(size || '').toLowerCase();
  return BANNER_SIZES.includes(normalized) ? normalized : DEFAULT_BANNER_SIZE;
}

export function getPosition(position) {
  const normalized = String(position || '').toLowerCase();
  return BANNER_POSITIONS.includes(normalized)
    ? normalized
    : DEFAULT_BANNER_POSITION;
}

export function isTruthy(value) {
  if (typeof value === 'boolean') return value;
  return ['true', '1', 'yes'].includes(String(value || '').toLowerCase());
}

export function getCtaType(type) {
  return CTA_TYPES[String(type || '').toLowerCase()] || 'internal';
}

export function mapContentfulCta(cta) {
  if (!cta?.fields) return null;

  const { fields } = cta;
  const colors = fields.colors?.fields || {};

  return {
    id: cta.sys?.id,
    label: fields.label,
    type: fields.type,
    url: fields.url,
    backgroundColor: fields.backgroundColor || colors.backgroundColor,
    backgroundHoverColor:
      fields.backgroundHoverColor || colors.backgroundHoverColor,
    fontColor: fields.fontColor || colors.fontColor,
    fontHoverColor: fields.fontHoverColor || colors.fontHoverColor,
    opacity: fields.opacity === undefined ? colors.opacity : fields.opacity,
  };
}

export function normalizeColor(color, fallback) {
  if (!color) return fallback;

  const value = String(color).trim();
  if (!value.startsWith('#')) {
    return HEX_REGEX.test(value) ? `#${value}` : value;
  }

  const hex = value.slice(1);
  if (HEX_REGEX.test(hex)) return `#${hex}`;
  if (HEX_CHARS_REGEX.test(hex) && hex.length > 6) return `#${hex.slice(0, 6)}`;

  return fallback;
}

function getOpacityPercent(opacity) {
  const value = Number(opacity);
  const percent =
    opacity === null || opacity === '' || !Number.isFinite(value)
      ? DEFAULT_CTA_OPACITY
      : value;

  return Math.min(Math.max(percent, 0), 100);
}

export function withOpacity(color, opacity = DEFAULT_CTA_OPACITY) {
  const normalized = normalizeColor(color);
  if (!normalized) return undefined;

  const percent = getOpacityPercent(opacity);
  const alpha = percent / 100;
  const hex = normalized.replace('#', '');

  if (HEX_REGEX.test(hex)) {
    const full =
      hex.length === 3
        ? hex
            .split('')
            .map((char) => char + char)
            .join('')
        : hex;

    const [red, green, blue] = [0, 2, 4].map((index) =>
      parseInt(full.slice(index, index + 2), 16)
    );

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  }

  const functional = normalized.match(FUNCTIONAL_COLOR_REGEX);
  if (functional) {
    const [, space, channels] = functional;

    if (!channels.includes(',')) return `${space}(${channels} / ${alpha})`;

    const [first, second, third] = channels
      .split(',')
      .map((channel) => channel.trim());

    return `${space}a(${first}, ${second}, ${third}, ${alpha})`;
  }

  // Named colors and any other CSS syntax: let the browser apply the alpha.
  return `color-mix(in srgb, ${normalized} ${percent}%, transparent)`;
}
